import React from 'react';
import { Controller } from 'react-hook-form';
import type { Control, FieldErrors, UseFormReturn } from 'react-hook-form';
import compactSelectStyles from '../../shared/compactSelectStyles';
import SPACheckbox from '../../shared/SPACheckbox';
import SPASelect from '../../shared/SPASelect';
import { Field } from '../../shared/formPanel';
import type { ProgramFormValues } from './programForm';

interface Option<T> {
  label: string;
  value: T;
}

// Mirrors Program::FONT_SIZES, Program::NOTEBOOK_COLORS (the labels are the stored values) and the
// legacy form's correlations_type collection.
const ARCHIVED_OPTIONS: Option<boolean>[] = [{ label: 'No', value: false }, { label: 'Yes', value: true }];
const FONT_SIZE_OPTIONS: Option<string>[] = ['small', 'medium', 'large', 'xlarge']
  .map(size => ({ label: size, value: size }));
const NOTEBOOK_COLOR_OPTIONS: Option<string>[] = ['Default (Green)', 'Program Color', 'Unit Color']
  .map(color => ({ label: color, value: color }));

type SelectName = 'archived' | 'font_size' | 'notebook_border_color';

interface SelectFieldProps<T> {
  control: Control<ProgramFormValues>;
  errors: FieldErrors<ProgramFormValues>;
  label: string;
  name: SelectName;
  options: Option<T>[];
  required?: boolean;
}

const SelectField = <T extends string | boolean>({
  control, errors, label, name, options, required = false,
}: SelectFieldProps<T>) => {
  const id = `program_${name}`;

  return (
    <Field error={errors[name]?.message} htmlFor={id} label={label} required={required}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <SPASelect<Option<T>, false>
            inputId={id}
            options={options}
            styles={compactSelectStyles}
            value={options.find(option => option.value === field.value) ?? null}
            onChange={option => field.onChange(option?.value)}
          />
        )}
        rules={required ? { validate: value => value !== '' || `${label} is required.` } : undefined}
      />
    </Field>
  );
};

interface ProgramSettingsFieldsProps {
  custom: boolean;
  form: UseFormReturn<ProgramFormValues>;
}

// The Program Settings grid, shared by the edit section and the New Program page. The legacy form
// only renders the integrated and source toggles for custom programs.
const ProgramSettingsFields = ({ custom, form }: ProgramSettingsFieldsProps) => {
  const { control, formState: { errors }, register, watch } = form;
  const isScience = watch('science');

  return (
    <>
      <div className="tw-grid tw-grid-cols-1 tw-gap-x-5 tw-gap-y-4 md:tw-grid-cols-3">
        <SelectField control={control} errors={errors} label="Archived" name="archived" options={ARCHIVED_OPTIONS} />
        <SelectField
          required
          control={control}
          errors={errors}
          label="Font Size"
          name="font_size"
          options={FONT_SIZE_OPTIONS}
        />
        <SelectField
          control={control}
          errors={errors}
          label="Notebook Border Color (K-5)"
          name="notebook_border_color"
          options={NOTEBOOK_COLOR_OPTIONS}
        />
      </div>

      <hr className="tw-my-5 tw-border-0 tw-border-t tw-border-solid tw-border-spa-border-secondary" />

      <div className="tw-grid tw-grid-cols-1 tw-gap-x-8 tw-gap-y-3.5 md:tw-grid-cols-2">
        <SPACheckbox {...register('science')}>Is a science program?</SPACheckbox>
        <SPACheckbox {...register('has_print_book')}>Has print book?</SPACheckbox>
        {custom && isScience && (
          <SPACheckbox {...register('integrated')}>Is integrated?</SPACheckbox>
        )}
        <SPACheckbox {...register('has_narration')}>Has narration?</SPACheckbox>
        <SPACheckbox {...register('show_online_assessment')}>Show online assessment option per lesson?</SPACheckbox>
        <SPACheckbox {...register('show_section_titles')}>Show section titles in student text?</SPACheckbox>
        {custom && (
          <SPACheckbox {...register('show_source')}>Display lesson source information?</SPACheckbox>
        )}
      </div>
    </>
  );
};

export default ProgramSettingsFields;
